<template>
    <v-card
        class="infocard"
        :class="{
            'infocard--dense': dense,
            'infocard--with-tags': visibleTags.length > 0,
        }"
        :elevation="elevation"
    >
        <div class="infocard__layout">
            <div class="infocard__main">
                <v-card-title
                    v-if="hasTitleBarContent"
                    class="d-flex"
                    :class="dense ? 'py-2 px-3 infocard__title-bar--dense' : 'pb-4'"
                >
                    <div class="flex-grow-1 d-flex align-center flex-wrap" style="gap: 8px;">
                        <v-icon v-if="titleIcon" small color="primary">{{ titleIcon }}</v-icon>
                        <span v-if="title">{{ title }}</span>
                        <slot name="title-append" />
                    </div>
                    <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                        <slot name="title-actions" />
                        <div class="font-italic font-weight-regular mr-2" v-if="extratitle">
                            {{ extratitle }}
                        </div>
                    </div>
                </v-card-title>
                <v-card-text :class="dense ? 'py-2 px-3' : 'pb-4'">
            <div v-if="error">
                {{ $translate("errorFetchingInformation") }}
            </div>
            <div v-else-if="!items?.length">
                <slot name="empty">
                    <div class="d-flex flex-grow-1 align-center">
                        <v-icon class="mr-2">mdi-alert-octagon-outline</v-icon
                        >{{ warning || $translate("noData") }}
                    </div>
                </slot>
            </div>
            <dl
                v-else-if="dense"
                class="infocard__dense-list"
                :style="{
                    gridTemplateColumns: `repeat(${denseCols}, minmax(0, 1fr))`,
                }"
            >
                <div
                    v-for="(item, key) in items"
                    :key="key"
                    class="infocard__dense-row"
                >
                    <dt class="infocard__dense-label">
                        <v-icon
                            v-if="item.icon"
                            size="14"
                            color="primary"
                            class="mr-1"
                        >
                            {{ iconName(item.icon) || item.icon }}
                        </v-icon>
                        <span>{{ item.title }}</span>
                        <v-tooltip
                            v-if="item.valueTooltip"
                            bottom
                            max-width="320"
                        >
                            <template #activator="{ on, attrs }">
                                <v-icon
                                    v-bind="attrs"
                                    v-on="on"
                                    x-small
                                    color="grey"
                                    class="ml-1 infocard__value-tooltip-icon"
                                >
                                    mdi-information-outline
                                </v-icon>
                            </template>
                            <span>{{ item.valueTooltip }}</span>
                        </v-tooltip>
                    </dt>
                    <span class="infocard__dense-leader" aria-hidden="true"></span>
                    <dd
                        class="infocard__dense-value copyable-row"
                        :class="[
                            item.color
                                ? `${item.color}--text`
                                : 'grey--text text--darken-2',
                        ]"
                    >
                        <span class="copyable-content">
                            <template v-if="item.link">
                                <component :is="item.bold ? 'b' : 'span'">
                                    <ScopedRouteLink
                                        v-if="isDict(item.link)"
                                        :to="item.link"
                                    >
                                        {{ item.value || "-" }}
                                    </ScopedRouteLink>
                                    <a
                                        v-else
                                        target="_blank"
                                        :href="item.link"
                                    >
                                        {{ item.value || $translate("link") }}
                                    </a>
                                </component>
                            </template>
                            <template v-else-if="item.lookup">
                                <Lookup
                                    :object="item.lookup.object"
                                    :params="item.lookup.params"
                                    >{{ item.value }}</Lookup
                                >
                            </template>
                            <template v-else-if="item.chips">
                                <span class="d-inline-flex flex-wrap gap-1">
                                    <v-chip
                                        v-for="(chip, chipIndex) in item.chips"
                                        :key="chipIndex"
                                        :small="chip.size === 'small'"
                                        :x-small="chip.size !== 'small'"
                                        :color="chip.color || 'grey'"
                                        text-color="white"
                                    >
                                        {{ chip.text || chip }}
                                    </v-chip>
                                </span>
                            </template>
                            <template v-else-if="item.value">
                                <component
                                    :is="item.bold ? 'b' : 'span'"
                                >{{ denseValueText(item) }}</component>
                            </template>
                            <template v-else-if="!item.action">-</template>
                            <template v-if="item.action">
                                <template v-if="item.value">&nbsp;-&nbsp;</template>
                                <a
                                    href="#"
                                    class="text-primary"
                                    @click.prevent="$emit(item.action)"
                                    >{{ item.action_text }}</a
                                >
                            </template>
                        </span>
                        <v-btn
                            v-if="getCopyableValue(item)"
                            x-small
                            icon
                            class="copy-btn"
                            @click.stop="copyToClipboard(getCopyableValue(item))"
                        >
                            <v-icon x-small>mdi-content-copy</v-icon>
                        </v-btn>
                    </dd>
                </div>
            </dl>
            <div
                v-else
                class="infocard__grid"
                :style="gridStyle"
            >
                <div
                    v-for="(item, key) in items"
                    :key="key"
                    class="infocard__grid-item d-flex"
                    :class="{ 'infocard__grid-item--large': item.large }"
                >
                    <div class="d-flex align-center infocard__item" :class="{ borders }">
                        <div class="iconwrapper" v-if="item.icon">
                            <v-icon size="12" color="primary" circle>
                                {{ iconName(item.icon) || item.icon }}
                            </v-icon>
                        </div>
                        <div class="flex-grow-1">
                            <div class="infocard__title d-flex align-center">
                                <span>{{ item.title }}</span>
                                <v-tooltip
                                    v-if="item.valueTooltip"
                                    bottom
                                    max-width="320"
                                >
                                    <template #activator="{ on, attrs }">
                                        <v-icon
                                            v-bind="attrs"
                                            v-on="on"
                                            x-small
                                            color="grey"
                                            class="ml-1 infocard__value-tooltip-icon"
                                        >
                                            mdi-information-outline
                                        </v-icon>
                                    </template>
                                    <span>{{ item.valueTooltip }}</span>
                                </v-tooltip>
                            </div>
                            <div
                                class="infocard__text copyable-row"
                                :class="[
                                    item.color
                                        ? `${item.color}--text`
                                        : 'grey--text text--darken-2',
                                ]"
                            >
                                <div class="copyable-content">
                                <template v-if="item.link">
                                    <component :is="item.bold ? 'b' : 'span'">
                                        <component
                                            v-if="item.preview"
                                            :is="getPreviewComponent(item.preview.type)"
                                            :order-id="item.preview.type === 'customerorder' || item.preview.type === 'providerorder' ? item.preview.id : undefined"
                                            :shipment-id="item.preview.type === 'shipment' ? item.preview.id : undefined"
                                            :receipt-id="item.preview.type === 'receipt' ? item.preview.id : undefined"
                                            :customer-id="item.preview.type === 'customer' ? item.preview.id : undefined"
                                            :invoice-id="item.preview.type === 'customerinvoice' || item.preview.type === 'providerinvoice' ? item.preview.id : undefined"
                                            :delay="1000"
                                        >
                                            <ScopedRouteLink
                                                v-if="isDict(item.link)"
                                                :to="item.link"
                                            >
                                                {{ item.value || "-" }}
                                            </ScopedRouteLink>
                                            <a target="_blank" v-else :href="item.link">
                                                {{ item.value || $translate("link") }}
                                            </a>
                                        </component>
                                        <template v-else>
                                            <ScopedRouteLink
                                                v-if="isDict(item.link)"
                                                :to="item.link"
                                            >
                                                {{ item.value || "-" }}
                                            </ScopedRouteLink>
                                            <a target="_blank" v-else :href="item.link">
                                                {{ item.value || $translate("link") }}
                                            </a>
                                        </template>
                                    </component>
                                </template>
                                <template v-else-if="item.lookup">
                                    <Lookup
                                        :object="item.lookup.object"
                                        :params="item.lookup.params"
                                        >{{ item.value }}</Lookup
                                    >
                                </template>

                                <template v-else>
                                    <template v-if="item.chips">
                                        <div class="d-flex flex-wrap gap-1">
                                            <v-chip
                                                v-for="(chip, chipIndex) in item.chips"
                                                :key="chipIndex"
                                                :small="chip.size === 'small'"
                                                :x-small="chip.size !== 'small'"
                                                :color="chip.color || 'grey'"
                                                text-color="white"
                                                class="mr-1 mb-1"
                                            >
                                                {{ chip.text || chip }}
                                            </v-chip>
                                        </div>
                                    </template>
                                    <template v-else-if="item.value">
                                        <div
                                            class="infocard__value-block"
                                            :class="{
                                                'infocard__value-block--clamped': shouldClamp(item, key),
                                                'infocard__value-block--inline':
                                                    !shouldClamp(item, key) && !item.pre,
                                            }"
                                            :style="clampStyle(item)"
                                        >
                                            <component
                                                class="whitespace-pre-wrap"
                                                :is="
                                                    item.pre
                                                        ? 'pre'
                                                        : item.bold
                                                        ? 'b'
                                                        : 'span'
                                                "
                                                :key="count"
                                                v-for="(line, count) in lines(item)"
                                            >
                                                <br v-if="count" />
                                                <template v-if="line">
                                                    <template v-if="isObject(line)">
                                                        <span>{{ line.title }}</span>
                                                        <template v-if="line.link">
                                                            -
                                                            <span
                                                                class="link"
                                                                @click="
                                                                    $emit(
                                                                        line.link.event,
                                                                        line.link.event_data
                                                                    )
                                                                "
                                                                >{{ line.link.title }}</span
                                                            >
                                                        </template>
                                                    </template>
                                                    <template v-else>
                                                        <span class="">{{ line }}</span>
                                                    </template>
                                                </template>
                                            </component>
                                        </div>
                                        <a
                                            v-if="isTruncatable(item, key)"
                                            href="#"
                                            class="infocard__expand-link text-primary"
                                            @click.prevent="toggleItemExpanded(key)"
                                        >
                                            {{
                                                isItemExpanded(key)
                                                    ? $translate("showLess")
                                                    : $translate("showMore")
                                            }}
                                        </a>
                                    </template>
                                    <template v-else-if="!item.action"> - </template>
                                </template>
                                <template v-if="item.action">
                                    <br v-if="lines(item).length > 1" />
                                    <template v-else-if="item.value"
                                        >&nbsp;-&nbsp;
                                    </template>
                                    <a
                                        href="#"
                                        v-if="item.action"
                                        class="text-primary"
                                        @click.prevent="$emit(item.action)"
                                        >{{ item.action_text }}</a
                                    >
                                </template>
                                </div>
                                <v-btn
                                    v-if="getCopyableValue(item)"
                                    x-small
                                    icon
                                    class="copy-btn"
                                    @click.stop="copyToClipboard(getCopyableValue(item))"
                                >
                                    <v-icon x-small>mdi-content-copy</v-icon>
                                </v-btn>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
                <slot />
                </v-card-text>
            </div>
            <v-card-title
                v-if="visibleTags.length"
                class="infocard__tags"
                :class="{ 'infocard__tags--dense': dense }"
            >
                <Tag
                    v-for="(tag, tagIndex) in visibleTags"
                    :key="tagIndex"
                    :tag="tag"
                    size="small"
                    chip-class="infocard__tag-chip"
                />
            </v-card-title>
        </div>
    </v-card>
</template>

<script>
import GlobalMixins from "@/mixins/global";
import CustomerOrderPreview from "./preview-customerorder.vue";
import ProviderOrderPreview from "./preview-providerorder.vue";
import ShipmentPreview from "./preview-shipment.vue";
import ReceiptPreview from "./preview-receipt.vue";
import CustomerPreview from "./preview-customer.vue";
import CustomerInvoicePreview from "./preview-customerinvoice.vue";
import ProviderInvoicePreview from "./preview-providerinvoice.vue";
import Tag from "./tag.vue";

export default {
    mixins: [GlobalMixins],
    components: {
        CustomerOrderPreview,
        ProviderOrderPreview,
        ShipmentPreview,
        ReceiptPreview,
        CustomerPreview,
        CustomerInvoicePreview,
        ProviderInvoicePreview,
        Tag,
    },
    data() {
        return {
            expandedItems: {},
        };
    },
    watch: {
        items: {
            handler() {
                this.expandedItems = {};
            },
            deep: true,
        },
    },
    props: {
        titleIcon: { type: String, default: null },
        title: {
            type: String,
            required: false,
        },
        extratitle: {
            type: String,
            required: false,
        },
        warning: {
            type: String,
            required: false,
        },
        elevation: {
            type: [String, Number],
            required: false,
        },
        items: {
            type: Array,
            required: false,
        },
        minColumnWidth: {
            type: Number,
            default: null,
            validator: value => value > 0,
        },
        cols: {
            type: Number,
            required: false,
            default: 12,
        },
        sm: {
            type: Number,
            required: false,
        },
        md: {
            type: Number,
            required: false,
        },
        lg: {
            type: Number,
            required: false,
        },
        xl: {
            type: Number,
            required: false,
        },
        borders: {
            type: Boolean,
            required: false,
            default: false,
        },
        error: {
            type: Boolean,
            required: false,
            default: false,
        },
        tags: {
            type: Array,
            required: false,
            default: () => [],
        },
        dense: {
            type: Boolean,
            required: false,
            default: false,
        },
        denseCols: {
            type: Number,
            required: false,
            default: 1,
            validator: (v) => Number.isInteger(v) && v >= 1 && v <= 6,
        },
    },
    computed: {
        visibleTags() {
            return (this.tags || []).filter((tag) => {
                if (typeof tag === "string") {
                    return Boolean(tag.trim());
                }
                if (!tag || typeof tag !== "object") {
                    return false;
                }
                return Boolean(tag.name || tag.text || tag.value);
            });
        },
        hasTitleBarContent() {
            const hasText = (value) =>
                typeof value === "string" && Boolean(value.trim());
            const titleActionNodes = this.$slots["title-actions"] || [];
            const hasTitleActions = titleActionNodes.some(
                (node) =>
                    node &&
                    (node.tag ||
                        (typeof node.text === "string" &&
                            Boolean(node.text.trim()))),
            );
            const titleAppendNodes = this.$slots["title-append"] || [];
            const hasTitleAppend = titleAppendNodes.some(
                (node) =>
                    node &&
                    (node.tag ||
                        (typeof node.text === "string" &&
                            Boolean(node.text.trim()))),
            );
            return (
                hasText(this.title) ||
                hasText(this.extratitle) ||
                hasTitleActions ||
                hasTitleAppend
            );
        },
        gridMaxCols() {
            const spans = [this.lg, this.md, this.sm, this.cols].filter(
                (value) => value != null && value > 0,
            );
            if (!spans.length) {
                return 1;
            }
            const minSpan = Math.min(...spans);
            return Math.max(1, Math.floor(12 / minSpan));
        },
        gridColMinPx() {
            if (this.minColumnWidth) return this.minColumnWidth;
            // Lower mins keep more columns longer as the card narrows
            // (delay 4→3 and 3→2). Higher mins switch earlier.
            const minWidthByMaxCols = {
                1: 280,
                2: 200,
                3: 170,
                4: 160,
                6: 140,
            };
            return minWidthByMaxCols[this.gridMaxCols] || 160;
        },
        gridStyle() {
            if (this.gridMaxCols === 1) {
                return {
                    "--infocard-max-cols": 1,
                    gridTemplateColumns: "1fr",
                };
            }
            return {
                "--infocard-max-cols": this.gridMaxCols,
                "--infocard-col-min-px": `${this.gridColMinPx}px`,
            };
        },
    },
    methods: {
        isObject(obj) {
            return typeof obj === "object";
        },
        isItemExpanded(key) {
            return !!this.expandedItems[key];
        },
        toggleItemExpanded(key) {
            this.$set(
                this.expandedItems,
                key,
                !this.expandedItems[key],
            );
        },
        shouldClamp(item, key) {
            return item.maxLines && !this.isItemExpanded(key);
        },
        clampStyle(item) {
            if (!item.maxLines) {
                return null;
            }
            return { "--infocard-clamp-lines": item.maxLines };
        },
        isTruncatable(item, key) {
            if (!item.maxLines) {
                return false;
            }
            const text = this.getCopyableValue(item);
            if (!text) {
                return false;
            }
            const threshold = Math.max(40, (item.maxLines || 3) * 35);
            return text.length > threshold;
        },
        denseValueText(item) {
            const parts = this.lines(item);
            return parts
                .map((line) => {
                    if (line && typeof line === "object") {
                        return line.title || "";
                    }
                    return line ?? "";
                })
                .filter((s) => s !== "")
                .join(" · ");
        },
        lines(item) {
            if (!item.value) {
                return [];
            }
            if (typeof item.value == "object") {
                return item.value;
            }
            return [item.value];
        },
        isDict(obj) {
            return typeof obj === "object";
        },
        getPreviewComponent(type) {
            const componentMap = {
                'customerorder': 'CustomerOrderPreview',
                'providerorder': 'ProviderOrderPreview',
                'shipment': 'ShipmentPreview',
                'receipt': 'ReceiptPreview',
                'customer': 'CustomerPreview',
                'customerinvoice': 'CustomerInvoicePreview',
                'providerinvoice': 'ProviderInvoicePreview',
            };
            return componentMap[type] || null;
        },
        getCopyableValue(item) {
            if (!item.value) return null;
            if (typeof item.value === 'string') {
                // Don't show copy button for empty values or dash
                if (item.value.trim() === '' || item.value.trim() === '-') {
                    return null;
                }
                return item.value;
            }
            if (Array.isArray(item.value)) {
                const filtered = item.value.filter(v => typeof v === 'string' && v.trim() !== '' && v.trim() !== '-');
                return filtered.length > 0 ? filtered.join('\n') : null;
            }
            return null;
        },
        async copyToClipboard(text) {
            try {
                await navigator.clipboard.writeText(text);
                this.addAlert({
                    text: this.$translate('copied'),
                    type: 'success',
                    timeout: 2000,
                });
            } catch (error) {
                console.error('Failed to copy:', error);
                this.addAlert({
                    text: this.$translate('copy_failed'),
                    type: 'error',
                    timeout: 2000,
                });
            }
        },
    },
    i18n: {
        messages: {
            en: {
                errorFetchingInformation: "Error fetching information",
                noData: "No data",
                link: "Link",
                copied: "Copied to clipboard",
                copy_failed: "Failed to copy",
                showMore: "Show more",
                showLess: "Show less",
            },
            fr: {
                errorFetchingInformation: "Erreur lors de la récupération des informations",
                noData: "Aucune donnée",
                link: "Lien",
                copied: "Copié dans le presse-papiers",
                copy_failed: "Échec de la copie",
                showMore: "Afficher plus",
                showLess: "Afficher moins",
            },
        },
    },
};
</script>

<style lang="scss">
@import "@/assets/css/variables.scss";
// Make the column that wraps an InfoCard a flex container so the card can
// stretch to the row height via flexbox. Safari does not resolve `height: 100%`
// on a child of a stretched flex column (it has no definite height), which made
// two cards on the same row render with unequal heights.
.col:has(> .infocard),
[class*="col-"]:has(> .infocard) {
    display: flex;
    flex-direction: column;
}
.col-6 > .infocard,
.col-12 > .infocard,
.col > .infocard,
[class*="col-"] > .infocard {
    flex: 1 1 auto;
    height: 100%;
}
.infocard {
    overflow: auto;
    display: flex;
    flex-direction: column;

    &__layout {
        display: flex;
        align-items: flex-start;
        width: 100%;
        flex: 1 1 auto;
        min-width: 0;
    }

    &__main {
        flex: 1 1 auto;
        min-width: 0;
    }

    &__tags {
        flex: 0 1 10rem;
        max-width: 10rem;
        min-width: 0;
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        align-items: flex-end;
        align-self: flex-start;
        gap: 4px;
        padding: 16px 16px 16px 8px;
        font-size: inherit;
        font-weight: inherit;
        line-height: 1;
        letter-spacing: normal;
        word-break: normal;

        .v-chip,
        .infocard__tag-chip {
            max-width: 100%;
            margin: 0;
            display: inline-flex;
            align-items: center;
        }

        .v-chip .v-chip__content {
            display: inline-flex;
            align-items: center;
            line-height: 1.25;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 100%;
        }
    }

    &__tags--dense {
        padding: 8px 12px 8px 4px;
    }

    a {
        text-decoration: none;
        &:hover {
            text-decoration: underline;
        }
    }
    .borders {
        border: 1px solid red;
    }
    .iconwrapper {
        align-self: center;
    }
    .iconwrapper,
    .iconwrapper > .v-icon {
        margin-right: 12px;
        height: 22px;
        width: 22px;
    }
    .iconwrapper > .v-icon {
        border-radius: 11px;
        background-color: rgba($color-primary, 0.1);
    }

    .infocard__title {
        color: $color-gs-extrabold;
        line-height: 18px;
        font-size: 11px !important;

        font-weight: 600;
    }
    .v-card__text {
        .infocard__text {
            line-height: 18px;
            color: $color-gs-medium;
            font-size: 11px !important;
        }
    }
    .infocard__item {
        width: 100%;
    }
    .infocard__grid {
        --infocard-gap: 16px;
        display: grid;
        gap: var(--infocard-gap);
        // Subtract gaps so max-cols actually fit (plain 100%/N overflows once
        // column-gap is applied and drops e.g. 3 → 2 columns too early).
        grid-template-columns: repeat(
            auto-fill,
            minmax(
                max(
                    var(--infocard-col-min-px, 160px),
                    calc(
                        (
                                100% - (var(--infocard-max-cols, 4) - 1) *
                                    var(--infocard-gap)
                            ) / var(--infocard-max-cols, 4)
                    )
                ),
                1fr
            )
        );
    }
    .infocard__grid-item {
        min-width: 0;
    }
    .infocard__grid-item--large {
        grid-column: 1 / -1;
    }
    .d-flex.align-center > .flex-grow-1 {
        min-width: 0;
        max-width: 100%;
    }
    .whitespace-pre-wrap {
        white-space: pre-wrap;
    }

    .infocard__value-block--inline {
        display: inline;
    }

    .infocard__value-block--clamped {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        -webkit-line-clamp: var(--infocard-clamp-lines, 3);
        line-clamp: var(--infocard-clamp-lines, 3);
    }

    .infocard__expand-link {
        display: inline-block;
        margin-top: 4px;
        font-size: 12px;
        text-decoration: none;
        &:hover {
            text-decoration: underline;
        }
    }
    
    .copyable-row {
        position: relative;
        display: inline-flex;
        align-items: baseline;
        max-width: 100%;
        min-width: 0;
    }
    
    .copyable-content {
        display: inline;
        max-width: 100%;
        min-width: 0;
        overflow-wrap: break-word;
        word-break: normal;
    }

    .copyable-content a {
        display: inline;
        overflow-wrap: break-word;
        word-break: normal;
    }

    // Preview wrappers default to inline-block, which becomes a full-width
    // box after wrap and pushes sibling action links ("Edit") onto a new line.
    .copyable-content > span > div,
    .copyable-content > b > div,
    .copyable-content > span > span,
    .copyable-content > b > span {
        display: inline !important;
    }
    
    .copy-btn {
        opacity: 0;
        transition: opacity 0.2s;
        display: inline-flex !important;
        vertical-align: middle;
        margin-left: 4px;
    }
    
    .copyable-row:hover .copy-btn {
        opacity: 1;
    }
    
    .copy-btn:hover {
        background-color: #f5f5f5 !important;
    }
}

.infocard--dense {
    .infocard__title-bar--dense {
        font-size: 13px;
        font-weight: 600;
        line-height: 1.2;
        min-height: 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }
    .v-card__text {
        font-size: 12px;
    }
    .infocard__dense-list {
        margin: 0;
        padding: 0;
        display: grid;
        column-gap: 16px;
        row-gap: 0;
    }
    .infocard__dense-row {
        display: flex;
        align-items: baseline;
        gap: 6px;
        padding: 7px 0;
        line-height: 1.3;
        min-height: 22px;
        border-bottom: 1px dashed rgba(0, 0, 0, 0.05);
        min-width: 0;
    }
    .infocard__dense-row:last-child {
        border-bottom: none;
    }
    .infocard__dense-label {
        display: inline-flex;
        align-items: center;
        flex: 0 0 auto;
        max-width: 55%;
        font-weight: 600;
        color: $color-gs-extrabold;
        font-size: 12px;
        margin: 0;
    }
    .infocard__dense-leader {
        flex: 1 1 auto;
        min-width: 8px;
        align-self: center;
        height: 1px;
        border-bottom: 1px dotted rgba(0, 0, 0, 0.18);
        opacity: 0.7;
    }
    .infocard__dense-value {
        flex: 0 1 auto;
        max-width: 60%;
        margin: 0;
        font-size: 12px;
        text-align: right;
        word-break: break-word;
    }
    .infocard__dense-value .copy-btn {
        margin-left: 2px;
    }
}
</style>

<style scoped>
.infocard__grid { font-size: 11px; }
</style>
